import { ApolloError } from "@apollo/client";
import {
    Alert,
    Headline,
    Loading,
    PrinterWrapper,
    StyleFunction,
    Surface,
    Text,
    useAuth,
    useThemedStyle,
} from "@venuepos/react-common";
import { GQInvoiceQuery, useInvoiceQuery } from "graphql-sdk";
import React from "react";
import { useTranslation } from "locales";
import QRCode from "react-qr-code";

import { RootStackScreenProps } from "../../navigation";
import { AdminContainer } from "../container";

type ScreenProps = RootStackScreenProps<"INVOICE">;

const barcodeRegex = /({{barcode:(.+?),(\w+),(\d+),(\d+),(\d+),(true|false)}})/;

export function InvoiceScreen({ route }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.invoice");

    const invoiceId = route.params.id;
    const { data, loading, error } = useInvoiceQuery({
        variables: {
            id: invoiceId,
        },
        fetchPolicy: "no-cache",
    });

    return (
        <AdminContainer>
            <Invoice loading={loading} error={error} data={data} />
        </AdminContainer>
    );
}

function Invoice({
    loading,
    error,
    data,
}: {
    loading: boolean;
    error?: ApolloError;
    data?: GQInvoiceQuery;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error.message,
                    }
                )}
            </Alert>
        );
    }

    if (!data?.invoice?.receipt) {
        return (
            <Alert type="warning">
                {t("backoffice.invoice.missing", "The receipt is missing.")}
            </Alert>
        );
    }

    const paymentReceipt = (receipt: string, key: string) => {
        return (
            <Surface style={styles.receiptWrapper} key={key}>
                <Text style={styles.receipt}>{receipt}</Text>
            </Surface>
        );
    };

    const externalReceipt = (receipt: string, key: string) => {
        // Remove the special blocks that should be rendered as QR
        let cleanedReceipt = receipt?.replace(barcodeRegex, "");

        // Remove center and h3 tags
        cleanedReceipt = cleanedReceipt.replace(/(\{\{(center|h3)\}\})/g, "");

        let qrCode = "";
        let size: number;
        const matches = receipt.match(barcodeRegex);
        if (matches && matches.length > 0) {
            qrCode = matches[2];
            size = Number.isInteger(matches[4])
                ? parseInt(matches[4], 10)
                : undefined;
        }

        return (
            <Surface style={styles.receiptWrapper} key={key}>
                <Text style={styles.receipt}>{cleanedReceipt}</Text>
                {qrCode !== "" && (
                    <QRCode // QRCode does not like our usual "styles.something"
                        // eslint-disable-next-line react-native/no-inline-styles
                        style={{ width: "100%" }}
                        size={size || 350}
                        value={qrCode}
                        level="L"
                    />
                )}
            </Surface>
        );
    };

    const externalItem = (
        receipt: string,
        integration: string,
        key: string
    ) => {
        if (integration === "venue-access") {
            return externalReceipt(receipt, `venue-access-${key}`);
        } else if (integration === "cmp-event") {
            return externalReceipt(receipt, `cmp-event-${key}`);
        } else if (integration === "FISKALY") {
            return externalReceipt(receipt, `fiskaly-${key}`);
        }

        return (
            <Surface style={styles.receiptWrapper} key={key}>
                <Text style={styles.receipt}>{receipt}</Text>
            </Surface>
        );
    };

    return (
        <Surface>
            <Headline size="h4" style={styles.headline}>
                {t("common.receipt", "Receipt")}
            </Headline>

            <PrinterWrapper>
                <Surface style={styles.receiptWrapper}>
                    <Text style={styles.receipt}>{data?.invoice.receipt}</Text>
                </Surface>
                {data?.invoice?.externalReceipts?.map(
                    (receipt, receiptIndex) => {
                        if (!receipt.receipt) {
                            return null;
                        }

                        return externalReceipt(
                            receipt.receipt,
                            `receipt-${receiptIndex}`
                        );
                    }
                )}

                {data?.invoice?.payments?.map((payment, paymentIndex) => {
                    if (!payment.cardReceipt) {
                        return null;
                    }

                    return paymentReceipt(
                        payment.cardReceipt,
                        `payment-${paymentIndex}`
                    );
                })}

                {data.invoice.items?.map((item, itemIndex) =>
                    item.external?.map((external, externalIndex) => {
                        if (!external.receipt) {
                            return null;
                        }

                        return externalItem(
                            external.receipt,
                            external.integration,
                            `external-${itemIndex}-${externalIndex}`
                        );
                    })
                )}
            </PrinterWrapper>
        </Surface>
    );
}

const styleFunc: StyleFunction = theme => ({
    headline: {
        marginBottom: 0,
    },
    receipt: {
        ...theme.fonts.monospace,
        fontSize: 14,
        textAlign: "center",
    },
    receiptWrapper: {
        padding: theme.spacingScale * 2,
        borderWidth: 1,
        borderColor: theme.colors.grey250,
        borderRadius: 0,

        // To ensure the printed receipt looks and feels like a POS receipt; with no maxWidth, the receipt just expands to the full paper width when it's printed.
        maxWidth: theme.dimensions.maxFormWidth,
        elevation: 3,
        marginBottom: 10,

        pageBreakInside: "avoid", // try printing the full content of a receipt on the same page.
    },
});
